import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Tooltip, message } from 'antd';

import { UserContext } from '@/app/UserProvider';
import CheckGreenIcon from '@/assets/check-green.svg?react';
import PopularIcon from '@/assets/popular.svg?react';
import { PaymentDrawer } from '@/pages/Pricing/components/PaymentDrawer';
import {
  generateDeviceId,
  getActivePlans,
  getPlanList,
  preOrder,
  submitOrder,
} from '@/services/billingService';
import type { PlanItem, PreOrderDetail } from '@/services/billingService';
import { getCookieDomain, setCookie } from '@/utils/cookie';
import { CURRENCY_SYMBOL_MAP } from '@/utils/currency';

import styles from './PricingSection.module.less';

// PricingPlan 继承 PlanItem 并扩展前端特有字段
interface PricingPlan extends PlanItem {
  features: string[];
  period: string;
  buttonText: string;
  buttonVariant: 'outline' | 'gray' | 'primary';
  isSubscribed?: boolean; // 是否已订阅
  isIncluded?: boolean; // 是否被已订阅的更高价套餐包含
  includedInPlanName?: string; // 包含该套餐的已订阅套餐名称
}

/**
 * 价格计划区域组件
 */
export const PricingSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isLoading: userLoading } = useContext(UserContext);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [preOrderData, setPreOrderData] = useState<PreOrderDetail | null>(null);
  const [loadingPreOrder, setLoadingPreOrder] = useState(false);
  const subscribingRef = useRef(false);

  useEffect(() => {
    // 等用户状态确定后再发请求，避免因 user 从 null 变为实际值触发两次请求导致按钮状态闪烁
    if (userLoading) return;

    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await getPlanList({
          page: 1,
          pageSize: 20,
          userId: user?.userId, // 根据 edit history 添加 userId 参数
        });
        // 如果用户已登录，获取活跃订阅列表
        let localSubscribedIds = new Set<string>();
        let localActivePlans: { planCode: string; chargePrice: number; planName: string }[] = [];
        if (user) {
          try {
            const activeResponse = await getActivePlans();

            if (activeResponse.status === 200 && activeResponse.data) {
              // 过滤掉 free 套餐，free 套餐不需要订阅检查
              const paidPlans = activeResponse.data.filter(
                (plan) => !plan.planCode.includes('free'),
              );
              localSubscribedIds = new Set(paidPlans.map((plan) => plan.planCode));
              localActivePlans = paidPlans.map((plan) => ({
                planCode: plan.planCode,
                chargePrice: plan.chargePrice,
                planName: plan.planName,
              }));
            }
          } catch (error) {
            console.error('Failed to fetch active plans:', error);
            // 获取订阅失败不影响套餐列表显示
          }
        }

        if (response.status === 200 && response.data.planList) {
          // 将API数据映射到前端数据结构
          const mappedPlans: PricingPlan[] = response.data.planList.map((apiPlan) => {
            // 根据 planCode 确定按钮样式
            let buttonVariant: 'outline' | 'gray' | 'primary' = 'gray';

            // 检查是否已订阅（free 套餐已在源头过滤，不会出现在 localSubscribedIds 中）
            const isSubscribed = localSubscribedIds.has(apiPlan.planCode);

            // 检查是否被已订阅的更高价套餐包含（当前套餐 chargePrice 低于已订阅套餐）
            const includingActivePlan = !isSubscribed
              ? localActivePlans.find((ap) => ap.chargePrice > apiPlan.chargePrice)
              : undefined;
            const isIncluded = !!includingActivePlan;
            const includedInPlanName = includingActivePlan?.planName;

            if (apiPlan.planCode.includes('free')) {
              buttonVariant = 'outline';
            } else if (isSubscribed || isIncluded || apiPlan.purchaseAllowed === false) {
              buttonVariant = 'gray'; // 已订阅、被包含或不允许购买显示为gray
            } else {
              buttonVariant = 'primary';
            }

            // 计算周期显示（根据语言使用不同字段）
            const isZh = i18n.resolvedLanguage === 'zh';
            const period = `/${isZh ? apiPlan.periodTypeCn : apiPlan.periodType}`;

            // 安全解析特性列表
            const parseFeatureList = (featureData: string | string[]): string[] => {
              try {
                if (Array.isArray(featureData)) {
                  return featureData;
                }
                if (typeof featureData === 'string') {
                  const parsed = JSON.parse(featureData) as string[];
                  return Array.isArray(parsed) ? parsed : [];
                }
                return [];
              } catch (error) {
                console.error('Failed to parse feature list:', error);
                return [];
              }
            };

            const features: string[] = parseFeatureList(
              isZh ? apiPlan.featureListCn : apiPlan.featureList,
            );

            // 根据 planCode 和订阅状态确定按钮文本
            let buttonText: string;
            if (apiPlan.planCode.includes('free')) {
              buttonText = t('pricing.freeButton');
            } else if (apiPlan.purchaseAllowed === false) {
              buttonText = t('pricing.purchaseNotAllowedButton');
            } else if (isSubscribed) {
              buttonText = t('pricing.currentButton');
            } else if (isIncluded) {
              buttonText = t('pricing.includedInPlan', { planName: includedInPlanName });
            } else if (!user) {
              buttonText = t('pricing.button');
            } else {
              buttonText = t('pricing.button');
            }

            return {
              ...apiPlan, // 继承所有 PlanItem 字段
              period,
              features,
              isSubscribed,
              isIncluded,
              includedInPlanName,
              buttonText,
              buttonVariant,
            };
          });

          setPlans(mappedPlans);
        }
      } catch (error) {
        console.error('Failed to fetch plans:', error);
        // 出错时可以设置默认值或显示错误信息
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [t, user, userLoading, i18n.resolvedLanguage]);

  // 处理订阅计划按钮点击
  const handleSubscribeClick = async (plan: PricingPlan) => {
    if (subscribingRef.current) return;
    subscribingRef.current = true;
    setTimeout(() => {
      subscribingRef.current = false;
    }, 1000);
    if (plan.planCode.includes('free')) {
      navigate('/download');
      return;
    }
    if (plan.purchaseAllowed === false) {
      return;
    }
    if (!plan.isSubscribed) {
      // 检查用户是否登录
      if (!user) {
        // 未登录，保存当前页面路径并跳转到登录页面
        sessionStorage.setItem('pendingRedirect', '/pricing');
        navigate('/login?redirect=/pricing');
        return;
      }

      // 已登录，点击订阅计划，调用预下单接口
      setSelectedPlan(plan);
      setLoadingPreOrder(true);

      try {
        // 使用 planCode 作为 skuId（或者后端返回的 plan 数据中应该包含 skuId）
        const response = await preOrder({ skuId: plan.skuId });
        // 修改判断条件：使用 response.status 而不是 response.code
        if (response.status === 200 && response.data && response.data.code === 200) {
          setPreOrderData(response.data);
          setDrawerOpen(true);
        } else {
          const errMsg = response.data?.msg || '预下单失败，请稍后重试';
          message.error(errMsg);
        }
      } catch (error: any) {
        message.error(error?.message || '预下单失败，请稍后重试');
      } finally {
        setLoadingPreOrder(false);
      }
    }
  };

  // 处理支付
  const handlePay = async (autoRenew: boolean) => {
    if (!selectedPlan || !preOrderData) {
      console.error('缺少必要的支付信息');
      return;
    }

    try {
      // 生成设备ID
      const deviceId = generateDeviceId();

      // 获取当前域名作为支付完成后的跳转地址
      // 末尾加 ?_=1 占位参数，防止第三方回跳时用 & 拼接导致 ?& 格式非法
      // const returnUrl = `${window.location.origin}/dashboard/plan?_=1`;
      const returnUrl = `${window.location.origin}/dashboard/plan`;

      // 调用支付接口
      const response = await submitOrder({
        orderOutNo: preOrderData.orderOutNo,
        skuId: selectedPlan.skuId, // 使用 planCode 作为 skuId
        returnUrl,
        deviceId,
        subEnabled: autoRenew,
        productDetailList: preOrderData.productDetailList, // 传递 productDetailList
      });

      if (response.status === 200 && response.data) {
        const { passToken, sid, userId, returnUrl = '', code, msg } = response.data;
        const cookieDomain = getCookieDomain();
        if (code !== 200) {
          message.error(msg || '出错了');
          return;
        }

        // 存储 passToken（使用 sid 作为 key 的一部分）
        if (passToken && sid) {
          setCookie(`${sid}_st`, passToken, {
            domain: cookieDomain,
            path: '/',
          });
        }

        // 存储 userId
        if (userId) {
          setCookie('userId', userId, {
            domain: cookieDomain,
            path: '/',
          });
        }

        // 存储 deviceId（从生成的 deviceId 变量获取）
        if (deviceId) {
          setCookie('did', deviceId, {
            domain: cookieDomain,
            path: '/',
          });
        }

        // 关闭抽屉
        setDrawerOpen(false);

        // 确保 cookie 设置完成后再打开支付窗口
        // 使用 setTimeout 确保浏览器有时间处理 cookie 设置
        setTimeout(() => {
          const paymentWindow = window.open(returnUrl, '_blank');

          if (!paymentWindow) {
            message.error('无法打开支付窗口，可能被浏览器拦截');
          }
        }, 100); // 延迟 100ms 确保 cookie 设置完成
      }
    } catch (error) {
      console.error('支付失败:', error);
      message.error('支付失败');
    }
  };

  // 处理关闭抽屉
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setPreOrderData(null);
  };

  if (loading) {
    return (
      <div className={styles.pricingSection}>
        <div className={styles.container}>
          <div className={styles.header}>
            <h1 className={styles.title}>{t('pricing.title')}</h1>
            <p className={styles.subtitle}>{t('pricing.subtitle')}</p>
          </div>
          <div className={styles.plansContainer}>
            <div className={styles.loading}>加载中...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pricingSection}>
      <div className={styles.container}>
        {/* 标题 */}
        <div className={styles.header}>
          <h1 className={styles.title}>{t('pricing.title')}</h1>
          <p className={styles.subtitle}>{t('pricing.subtitle')}</p>
        </div>

        {/* 价格卡片 */}
        <div className={styles.plansContainer}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`${styles.planCard} ${plan.recommended === 1 ? styles.popular : ''}`}
            >
              <div className={styles.planContent}>
                <div className={styles.planHeader}>
                  <div className={styles.planNameRow}>
                    <h3 className={styles.planName}>{plan.planName}</h3>
                    {plan.recommended === 1 && (
                      <div className={styles.popularBadge}>
                        <PopularIcon />
                        <span>{t('pricing.popular')}</span>
                      </div>
                    )}
                  </div>
                  <div className={styles.priceWrapper}>
                    <div className={styles.priceContainer}>
                      <div className={styles.priceGroup}>
                        <span className={styles.price}>
                          {CURRENCY_SYMBOL_MAP[plan.currencyUnit || 'CNY'] || '¥'}
                          {plan.purchased ? plan.chargePrice : plan.actualPrice}
                        </span>
                        <span className={styles.period}>{plan.period}</span>
                      </div>
                      {!plan.purchased && plan.chargePrice > plan.actualPrice && (
                        <span className={styles.originalPrice}>
                          {CURRENCY_SYMBOL_MAP[plan.currencyUnit || 'CNY'] || '¥'}
                          {plan.chargePrice}
                          {plan.period}
                        </span>
                      )}
                    </div>
                    <p className={styles.renewalNotice}>
                      {!plan.purchased &&
                        plan.chargePrice > plan.actualPrice &&
                        t('pricing.renewalNotice', {
                          price: `${CURRENCY_SYMBOL_MAP[plan.currencyUnit || 'CNY'] || '¥'}${plan.chargePrice}${plan.period}`,
                        })}
                    </p>
                  </div>
                </div>

                <ul className={styles.featuresList}>
                  {plan.features.map((feature, index) => (
                    <li key={index} className={styles.featureItem}>
                      <CheckGreenIcon />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Tooltip
                title={
                  plan.planCode.includes('free')
                    ? ''
                    : plan.purchaseAllowed === false
                      ? t('pricing.purchaseNotAllowedTooltip')
                      : plan.isSubscribed
                        ? t('pricing.subscribedTooltip')
                        : plan.isIncluded
                          ? t('pricing.includedInPlan', { planName: plan.includedInPlanName })
                          : ''
                }
                placement="bottom"
              >
                <button
                  className={`${styles.planButton} ${styles[plan.buttonVariant]}`}
                  onClick={() => handleSubscribeClick(plan)}
                  disabled={
                    !plan.planCode.includes('free') &&
                    (plan.isSubscribed || plan.isIncluded || plan.purchaseAllowed === false)
                  }
                >
                  {plan.buttonText}
                </button>
              </Tooltip>
            </div>
          ))}
        </div>

        {/* 支付抽屉 */}
        {selectedPlan && (
          <PaymentDrawer
            open={drawerOpen}
            loading={loadingPreOrder}
            onClose={handleCloseDrawer}
            planData={preOrderData}
            onPay={handlePay}
          />
        )}
      </div>
    </div>
  );
};
